/**
 * CARD VIEW MODEL (B8). Everything the card prints for one place, derived from
 * the store's data once (pure; unit-tested). Copy follows design-spec §C / §F.
 */
import type { Category, CategoryKind, Place, RouteRecord } from "@/contracts/data";
import { displayTravel } from "@/contracts/data";
import type { AppState } from "@/contracts/store";
import { selectCategory, selectPlace, selectVisiblePlaces } from "@/store/selectors";
import {
  arrowRotation,
  directionWord,
  extraPinsNote,
  fmtKm,
  futureNote,
  futureStatusFact,
  gmapsHref,
  localityLine,
  minFig,
  noteLines,
  numbersNote,
  peekLine,
  statusWord,
  stripSegments,
  stripTicks,
  totalLine,
  turnRows,
  viaLine,
  viaRoads,
  type StripSegment,
  type TurnRow,
} from "./format";

export interface FactView {
  key: "km" | "min" | "status" | "crow" | "dir";
  label: string; //          dt text (visible)
  value?: string; //         figure
  unit?: string;
  /** Direction fact: arrow rotation (deg) and the spoken text. */
  rotate?: number;
  spoken?: string;
}

export interface CardView {
  place: Place;
  category: Category | null;
  kind: CategoryKind | null;
  icon: string;
  status: Place["status"];
  statusLabel: string | null;
  name: string;
  locality: string | null;
  peek: string | null;
  route: RouteRecord | null;
  strip: { segs: StripSegment[]; ticks: number[] } | null;
  facts: FactView[];
  numbersNote: string | null;
  via: string | null;
  notes: string[];
  extra: string | null;
  futureNote: string | null;
  siteNote: string | null;
  turns: TurnRow[];
  total: string | null;
  /** "Take the drive" is offered (a real route to an existing place). */
  canDrive: boolean;
  gmaps: string;
}

export const SITE_NOTE = "The site outline is approximate, traced from the master plan.";

export function buildView(s: AppState, id: string | null): CardView | null {
  const place = selectPlace(s, id);
  if (!place) return null;
  const category = selectCategory(s, place.category);
  const kind = category?.kind ?? null;
  const travel = displayTravel(place);
  const route = (place.route && s.routes?.routes.find((r) => r.id === place.route?.routeId)) || null;
  const isFuture = kind === "future";
  const isSite = !!place.isSite;
  const roadFeature = kind === "road" && place.geometryRef ? s.geo?.roads.features.find((f) => f.properties.id === place.geometryRef) : undefined;
  const futureFeature = isFuture && place.geometryRef ? s.geo?.future.features.find((f) => f.properties.id === place.geometryRef) : undefined;
  const dir = directionWord(place.bearing);

  const dirFact: FactView = { key: "dir", label: dir, rotate: arrowRotation(place.bearing), spoken: `${dir} of the site` };
  let facts: FactView[] = [];
  if (isFuture) {
    // No "planned length": the corridors are schematic or indicative lines (QA L3-1).
    const st = futureStatusFact(place.status);
    facts = [
      ...(st ? [{ key: "status" as const, label: st.label, value: st.value }] : []),
      { key: "crow", label: "From the site", value: fmtKm(place.crowKm), unit: "km" },
      dirFact,
    ];
  } else if (!isSite && travel) {
    const m = minFig(travel.minutes);
    facts = [
      { key: "km", label: "By road", value: fmtKm(travel.km), unit: "km" },
      { key: "min", label: "Off-peak drive", value: m.value, unit: m.unit },
      dirFact,
    ];
  }

  const showRoute = !isFuture && !isSite && !!route;
  const segs = showRoute && route ? stripSegments(route.nameSpans, route.lengthM) : [];
  return {
    place,
    category,
    kind,
    icon: place.icon ?? category?.icon ?? "mapPin",
    status: place.status,
    statusLabel: statusWord(place.status),
    name: place.name,
    locality: localityLine(place, kind, roadFeature?.properties.ref),
    peek: isFuture || isSite ? null : peekLine(place),
    route: showRoute ? route : null,
    strip: showRoute ? { segs, ticks: stripTicks(segs) } : null,
    facts,
    numbersNote: facts.length && !isFuture ? numbersNote(place) : null,
    via: isFuture || isSite ? null : viaLine(viaRoads(place.route?.via, place.name)),
    notes: noteLines(place),
    extra: extraPinsNote(place.extraPins),
    futureNote: futureFeature ? futureNote(futureFeature.properties.source, futureFeature.properties.id) : null,
    siteNote: isSite ? SITE_NOTE : null,
    turns: showRoute && route ? turnRows(route.nameSpans, route.lengthM) : [],
    total: showRoute ? totalLine(place) : null,
    canDrive: showRoute && !!travel,
    gmaps: gmapsHref(place),
  };
}

/** The route strip's track width and label size for a layout (§A card widths, §C.1). */
export interface StripGeometry {
  trackPx: number;
  fontPx: number;
}

/**
 * The strip's track from the card's own CSS width (card.css), so the labels can be laid out without
 * reading layout: card − strip margins (8 + 8) − the Whorl's side (36) − the pin's side (72; kiosk 90).
 * Desktop 1440: 392 → 268. Large 420 → 296. Compact 360 → 236. Kiosk 460 → 318 at 11.4 px.
 * Mobile: the card is the sheet's full width.
 */
export function stripGeometry(o: { layout: string; kiosk: boolean; embedded: boolean; vw: number }): StripGeometry {
  if (o.kiosk && !o.embedded) return { trackPx: 460 - 16 - 36 - 90, fontPx: 11.4 };
  let card: number;
  if (o.embedded || o.layout === "mobile") card = o.vw;
  else if (o.vw >= 1680) card = 420;
  else if (o.vw <= 1099) card = 360;
  else card = Math.min(392, o.vw - 108);
  return { trackPx: Math.max(120, card - 16 - 36 - 72), fontPx: 9.5 };
}

/** "{i} of {n}" over the visible list (the site excluded); i = 0 when the place is not listed (no pager), null with no selection. */
export function positionOf(s: AppState, id: string | null): { i: number; n: number } | null {
  if (!id) return null;
  const list = selectVisiblePlaces(s).filter((p) => !p.isSite);
  const i = list.findIndex((p) => p.id === id);
  return i < 0 ? { i: 0, n: list.length } : { i: i + 1, n: list.length };
}
